import { AxiosResponse } from "axios";
import { api, CustomAxiosRequestConfig, ENDPOINTS } from "@app/api";
import type {
  IrregularMaintenanceItemChoiceMaster,
  MaintenanceCategoryMaster,
  MaintenanceReportsMasterData,
  MaintenanceReportsMasterDataResponse,
  MaintenanceSelectOption,
  MaintenanceTypeMaster,
  MaintenanceWorkScopeMaster,
  PeriodicMaintenanceBulkImportRow,
  PeriodicMaintenanceMasterDataResponse,
  PeriodicMaintenanceMasterDataRowDetail,
  PeriodicMaintenanceSchedule,
  RegularMaintenanceItemChoiceMaster,
} from "@app/types/maintenanceMasterData.types";

export const IRREGULAR_MAINTENANCE_ITEM_OTHER_CODE = "OTHER";
export const MAINTENANCE_CATEGORY_FILTERS_CODE = "FILTERS";
export const MAINTENANCE_INTERVAL_OTHERS_CODE = "OTHER";
export const MAINTENANCE_CATEGORY_OTHERS_CODE = "OTHERS";
export const MAINTENANCE_TYPE_ALL_CODE = "ALL";
export const MAINTENANCE_TYPE_OTHER_CODE = "OTHER";

function sortByPosition<T extends { position: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.position - b.position);
}

export function mapRegularMaintenanceItemsToOptions(
  items: RegularMaintenanceItemChoiceMaster[] | undefined,
): MaintenanceSelectOption[] {
  return sortByPosition(items ?? [])
    .map((item) => ({
      id: item.regularMaintenanceItemChoiceId,
      label: item.regularMaintenanceItemChoiceName,
      code: item.regularMaintenanceItemChoiceCode,
    }))
    .filter((item) => item.code !== MAINTENANCE_INTERVAL_OTHERS_CODE);
}

export function mapMaintenanceWorkScopeToOptions(
  items: MaintenanceWorkScopeMaster[] | undefined,
): MaintenanceSelectOption[] {
  return sortByPosition(items ?? []).map((item) => ({
    id: item.maintenanceWorkScopeId,
    label: item.maintenanceWorkScopeName,
  }));
}

export function mapMaintenanceCategoriesToOptions(
  categories: MaintenanceCategoryMaster[] | undefined,
): MaintenanceSelectOption[] {
  return sortByPosition(categories ?? []).map((item) => ({
    id: item.maintenanceCategoryId,
    label: item.maintenanceCategoryName,
  }));
}

export function mapMaintenanceTypesToOptions(
  types: MaintenanceTypeMaster[] | undefined,
  options?: { excludeAll?: boolean },
): MaintenanceSelectOption[] {
  const excludeAll = options?.excludeAll ?? true;
  return sortByPosition(types ?? [])
    .filter(
      (item) =>
        !excludeAll || item.maintenanceTypeCode !== MAINTENANCE_TYPE_ALL_CODE,
    )
    .map((item) => ({
      id: item.maintenanceTypeId,
      label: item.maintenanceTypeName,
    }));
}

export function getMaintenanceCategoryCode(
  categoryId: string,
  categories: MaintenanceCategoryMaster[],
): string {
  return (
    categories.find((item) => item.maintenanceCategoryId === categoryId)
      ?.maintenanceCategoryCode ?? ""
  );
}

export function isFiltersMaintenanceCategory(categoryCode: string): boolean {
  return categoryCode === MAINTENANCE_CATEGORY_FILTERS_CODE;
}

export function isOthersMaintenanceCategory(categoryCode: string): boolean {
  return categoryCode === MAINTENANCE_CATEGORY_OTHERS_CODE;
}

/** Categories that hide Refill/Replace and Added amount on the item form. */
export function shouldHideMaintenanceRefillReplaceFields(
  categoryCode: string,
): boolean {
  return (
    isFiltersMaintenanceCategory(categoryCode) ||
    isOthersMaintenanceCategory(categoryCode)
  );
}

export function getMaintenanceTypeCode(
  maintenanceTypeId: string,
  types: MaintenanceTypeMaster[],
): string {
  return (
    types.find((item) => item.maintenanceTypeId === maintenanceTypeId)
      ?.maintenanceTypeCode ?? ""
  );
}

export function isMaintenanceTypeOtherChoice(
  maintenanceTypeId: string,
  types: MaintenanceTypeMaster[],
): boolean {
  return (
    getMaintenanceTypeCode(maintenanceTypeId, types) ===
    MAINTENANCE_TYPE_OTHER_CODE
  );
}

export function isMaintenanceTypeAllChoice(
  maintenanceTypeId: string,
  types: MaintenanceTypeMaster[],
): boolean {
  return (
    getMaintenanceTypeCode(maintenanceTypeId, types) ===
    MAINTENANCE_TYPE_ALL_CODE
  );
}

/** FILTERS: each specific type maps to one irregular item (not for ALL). */
export function resolveIrregularMaintenanceItemForFiltersType(
  categoryId: string,
  maintenanceTypeId: string,
  irregularItems: IrregularMaintenanceItemChoiceMaster[],
  types?: MaintenanceTypeMaster[],
): MaintenanceSelectOption | null {
  if (!categoryId || !maintenanceTypeId) {
    return null;
  }
  if (types && isMaintenanceTypeAllChoice(maintenanceTypeId, types)) {
    return null;
  }
  const maintenanceTypeCode = types
    ? getMaintenanceTypeCode(maintenanceTypeId, types)
    : "";
  if (maintenanceTypeCode === MAINTENANCE_TYPE_ALL_CODE) {
    return null;
  }
  const options = getIrregularMaintenanceItemOptionsForCategory(
    categoryId,
    irregularItems,
    {
      categoryCode: MAINTENANCE_CATEGORY_FILTERS_CODE,
      maintenanceTypeId,
      maintenanceTypeCode,
    },
  );
  return options[0] ?? null;
}

export function getIrregularMaintenanceItemOtherChoiceId(
  irregularItems: IrregularMaintenanceItemChoiceMaster[],
): string | null {
  return (
    irregularItems.find(
      (item) =>
        item.irregularMaintenanceItemChoiceCode ===
        IRREGULAR_MAINTENANCE_ITEM_OTHER_CODE,
    )?.irregularMaintenanceItemChoiceId ?? null
  );
}

export function isIrregularMaintenanceItemOtherChoice(
  maintenanceItemId: string,
  irregularItems: IrregularMaintenanceItemChoiceMaster[],
): boolean {
  if (!maintenanceItemId) {
    return false;
  }
  const otherId = getIrregularMaintenanceItemOtherChoiceId(irregularItems);
  return Boolean(otherId && maintenanceItemId === otherId);
}

export type IrregularMaintenanceItemFilterOptions = {
  categoryCode?: string;
  maintenanceTypeId?: string;
  maintenanceTypeCode?: string;
};

/** Irregular maintenance items for a category; appends global "OTHER" choice when present. */
export function getIrregularMaintenanceItemOptionsForCategory(
  categoryId: string,
  irregularItems: IrregularMaintenanceItemChoiceMaster[],
  filterOptions?: IrregularMaintenanceItemFilterOptions,
): MaintenanceSelectOption[] {
  if (!categoryId) {
    return [];
  }

  const categoryCode = filterOptions?.categoryCode ?? "";
  const isFilters = isFiltersMaintenanceCategory(categoryCode);

  if (isFilters && !filterOptions?.maintenanceTypeId) {
    return [];
  }

  const otherId = getIrregularMaintenanceItemOtherChoiceId(irregularItems);
  const otherChoice = irregularItems.find(
    (item) => item.irregularMaintenanceItemChoiceId === otherId,
  );

  let forCategory = irregularItems.filter(
    (item) =>
      item.maintenanceCategoryId === categoryId &&
      item.irregularMaintenanceItemChoiceId !== otherId,
  );

  if (
    isFilters &&
    filterOptions?.maintenanceTypeId &&
    filterOptions.maintenanceTypeCode !== MAINTENANCE_TYPE_ALL_CODE
  ) {
    forCategory = forCategory.filter(
      (item) => item.maintenanceTypeId === filterOptions.maintenanceTypeId,
    );
  }

  const options: MaintenanceSelectOption[] = sortByPosition(forCategory).map(
    (item) => ({
      id: item.irregularMaintenanceItemChoiceId,
      label: item.irregularMaintenanceItemChoiceName,
      code: item.irregularMaintenanceItemChoiceCode,
    }),
  );

  const typeCode = filterOptions?.maintenanceTypeCode ?? "";
  const includeOtherChoice =
    !isFilters ||
    typeCode === MAINTENANCE_TYPE_ALL_CODE ||
    typeCode === MAINTENANCE_TYPE_OTHER_CODE;

  if (
    includeOtherChoice &&
    otherChoice &&
    otherId &&
    !options.some((option) => option.id === otherId)
  ) {
    options.push({
      id: otherChoice.irregularMaintenanceItemChoiceId,
      label: otherChoice.irregularMaintenanceItemChoiceName,
      code: otherChoice.irregularMaintenanceItemChoiceCode,
    });
  }

  return options;
}

export const apiGetMaintenanceReportsMasterData = (): Promise<
  AxiosResponse<MaintenanceReportsMasterDataResponse>
> => {
  return api.get(ENDPOINTS.maintenanceReportsMasterData, {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export async function fetchMaintenanceReportsMasterData(): Promise<MaintenanceReportsMasterData> {
  const response = await apiGetMaintenanceReportsMasterData();
  return (
    response.data?.data ?? {
      maintenanceCategory: [],
      maintenanceTypes: [],
      irregularMaintenanceItems: [],
      regularMaintenanceItems: [],
      maintenanceWorkScope: [],
    }
  );
}

export type MaintenanceFormMasterData = {
  intervalOptions: MaintenanceSelectOption[];
  workScopeOptions: MaintenanceSelectOption[];
  categoryOptions: MaintenanceSelectOption[];
  maintenanceCategories: MaintenanceCategoryMaster[];
  maintenanceTypes: MaintenanceTypeMaster[];
  irregularMaintenanceItems: IrregularMaintenanceItemChoiceMaster[];
};

export function mapMaintenanceReportsMasterDataToFormOptions(
  data: MaintenanceReportsMasterData,
): MaintenanceFormMasterData {
  return {
    intervalOptions: mapRegularMaintenanceItemsToOptions(
      data.regularMaintenanceItems,
    ),
    workScopeOptions: mapMaintenanceWorkScopeToOptions(
      data.maintenanceWorkScope,
    ),
    categoryOptions: mapMaintenanceCategoriesToOptions(
      data.maintenanceCategory,
    ),
    maintenanceCategories: data.maintenanceCategory ?? [],
    maintenanceTypes: data.maintenanceTypes ?? [],
    irregularMaintenanceItems: data.irregularMaintenanceItems ?? [],
  };
}

export async function fetchMaintenanceFormMasterData(): Promise<MaintenanceFormMasterData> {
  const data = await fetchMaintenanceReportsMasterData();
  return mapMaintenanceReportsMasterDataToFormOptions(data);
}

/** @deprecated Use fetchMaintenanceFormMasterData */
export async function fetchMaintenanceIntervalAndWorkScopeOptions(): Promise<{
  intervalOptions: MaintenanceSelectOption[];
  workScopeOptions: MaintenanceSelectOption[];
}> {
  const master = await fetchMaintenanceFormMasterData();
  return {
    intervalOptions: master.intervalOptions,
    workScopeOptions: master.workScopeOptions,
  };
}

export const apiGetPeriodicMaintenanceSchedule = async (
  modelType: string,
  intervalCode: string,
): Promise<PeriodicMaintenanceSchedule> => {
  const response = await api.get(
    ENDPOINTS.apiPeriodicMaintenanceSchedule(modelType, intervalCode),
    {
      skipGlobalLoader: true,
    } as CustomAxiosRequestConfig,
  );
  return (
    response.data?.data ?? {
      workItems: [],
    }
  );
};

export const apiGetPeriodicMaintenanceAvailability = async (
  modelType: string,
): Promise<boolean> => {
  const response = await api.get(
    ENDPOINTS.apiPeriodicMaintenanceAvailability(modelType),
  );
  return response.data?.data ?? true;
};

export const apiGetPeriodicMaintenanceMasterData = (
  page: string,
  limit: string,
  order?: string,
  orderBy?: string,
  search?: string,
  work?: string,
  countryCode?: string,
): Promise<AxiosResponse<PeriodicMaintenanceMasterDataResponse>> => {
  return api.get(
    ENDPOINTS.periodicMaintenanceMasterList(
      page,
      limit,
      order,
      orderBy,
      search,
      work,
      countryCode,
    ),
    { skipGlobalLoader: true } as CustomAxiosRequestConfig,
  );
};

export const apiGetPeriodicMaintenanceTemplateFileUrl =
  (): Promise<AxiosResponse> => {
    return api.get(ENDPOINTS.periodicMaintenanceTemplateFileUrl);
  };

export const apiPostPeriodicMaintenanceBulkImport = (payload: {
  rows: PeriodicMaintenanceBulkImportRow[];
}): Promise<AxiosResponse> => {
  return api.post(ENDPOINTS.periodicMaintenanceBulkImport, payload);
};

export const apiDeletePeriodicMaintenanceMasterData = (
  periodicMaintenanceMachineItemIds: string[],
): Promise<AxiosResponse> => {
  return api.delete(ENDPOINTS.periodicMaintenanceMasterDataDelete, {
    data: { periodicMaintenanceMachineItemIds },
  } as CustomAxiosRequestConfig);
};

export const apiGetPeriodicMaintenanceMasterDataRowDetail = (
  periodicMaintenanceMachineItemId: string,
): Promise<AxiosResponse<{ data: PeriodicMaintenanceMasterDataRowDetail }>> => {
  return api.get(
    ENDPOINTS.periodicMaintenanceMasterDataDetail(
      periodicMaintenanceMachineItemId,
    ),
  );
};

export const apiUpdatePeriodicMaintenanceMasterData = (
  periodicMaintenanceMachineItemId: string,
  payload: PeriodicMaintenanceBulkImportRow,
): Promise<AxiosResponse> => {
  return api.put(
    ENDPOINTS.periodicMaintenanceMasterDataUpdate(
      periodicMaintenanceMachineItemId,
    ),
    payload,
  );
};

export const apiGetMaintenanceReportTemplates = (): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.maintenanceReportTemplates, {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};
